// MorningGate — the forced first-tap of every new day. When morningDone is
// false the world dims and this card sits on top: one breath, one intention,
// one tap. Ten seconds max, then the world opens. Per the Sims-reboot brief,
// this is THE entry point for play.
//
// The original MorningRitual screen still exists for the "I want to redo it"
// path; this gate is the always-on auto-experience.

import { useState } from 'react';
import { motion } from 'framer-motion';
import { dayKeyFromDate } from '@biz-kid/engine';
import { useDispatch } from '@biz-kid/store';
import { Card, Eyebrow, Button } from '../../ui/kit.js';
import { CartoonMiko as Miko } from '../../ui/cartoon.js';
import { useLife } from '../../life/store.js';

type Intention = { id: string; label: string; emoji: string };
const INTENTIONS: Intention[] = [
  { id: 'focus', label: 'Focus', emoji: '🎯' },
  { id: 'kindness', label: 'Kindness', emoji: '🫶' },
  { id: 'courage', label: 'Courage', emoji: '🦁' },
];

export function MorningGate({ onDone }: { onDone: () => void }) {
  const dispatch = useDispatch();
  const playerName = useLife().playerName ?? 'Boss';
  const [pickedId, setPickedId] = useState<string | null>(null);

  const begin = (intentionId: string) => {
    // All three are "growth" intentions — they map to the disciplined mindset
    // edge in the engine. The chip the kid picks is the tactile gesture.
    setPickedId(intentionId);
    dispatch({
      kind: 'CompleteWakeUp',
      dayKey: dayKeyFromDate(new Date()),
      mindset: 'growth',
      mindsetTags: [intentionId],
    });
    // tiny delay so the kid sees their pick highlight before the gate dismisses
    setTimeout(onDone, 350);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 96,
        display: 'grid', placeItems: 'center', padding: 18,
        // Dim the world behind, don't black it out — the kid sees they're
        // ABOUT to step into something that's already there.
        background: 'color-mix(in srgb, var(--bg) 80%, transparent)',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <motion.div
        initial={{ y: 12, opacity: 0, scale: 0.97 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 22 }}
        style={{ width: '100%', maxWidth: 360 }}
      >
        <Card style={{ textAlign: 'center' }}>
          <Eyebrow color="var(--brand)">Good morning, {playerName}.</Eyebrow>
          <h2 style={{ margin: '4px 0 12px', fontSize: 20, fontWeight: 900, color: 'var(--text)' }}>
            TAKE ONE BREATH.
          </h2>

          {/* Breath circle — single 4-second inhale + 4-second exhale loop.
              Soft brand fill so dark mode reads OK. */}
          <div style={{ display: 'grid', placeItems: 'center', height: 130, marginBottom: 6 }}>
            <motion.div
              animate={{ scale: [0.62, 1, 0.62], opacity: [0.7, 1, 0.7] }}
              transition={{ duration: 8, ease: 'easeInOut', repeat: Infinity }}
              style={{
                width: 96, height: 96, borderRadius: '50%',
                background: 'radial-gradient(circle at 35% 30%, var(--brand-soft), var(--brand))',
                boxShadow: '0 0 24px color-mix(in srgb, var(--brand) 40%, transparent), inset 0 -6px 0 rgba(0,0,0,0.10)',
                display: 'grid', placeItems: 'center',
              }}
            >
              <Miko size={56} mood="thinking" />
            </motion.div>
          </div>

          <p style={{ margin: '4px 0 14px', fontSize: 13.5, color: 'var(--text-2)', fontWeight: 700 }}>
            PICK YOUR ENERGY:
          </p>

          {/* Three intention chips — picking one IS the tap that opens the world. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {INTENTIONS.map((it) => {
              const picked = pickedId === it.id;
              return (
                <motion.button
                  key={it.id}
                  type="button"
                  onClick={() => pickedId === null && begin(it.id)}
                  disabled={pickedId !== null && !picked}
                  whileTap={{ scale: 0.94 }}
                  whileHover={pickedId === null ? { y: -2 } : undefined}
                  style={{
                    padding: '12px 8px',
                    borderRadius: 14,
                    border: `2px solid ${picked ? 'var(--brand)' : 'var(--border)'}`,
                    background: picked ? 'var(--brand-soft)' : 'var(--surface-2)',
                    color: 'var(--text)',
                    fontFamily: 'inherit', fontWeight: 800, fontSize: 14,
                    cursor: pickedId === null ? 'pointer' : 'default',
                    opacity: pickedId !== null && !picked ? 0.5 : 1,
                    display: 'grid', placeItems: 'center', gap: 4,
                  }}
                >
                  <span style={{ fontSize: 28 }}>{it.emoji}</span>
                  <span>{it.label}</span>
                </motion.button>
              );
            })}
          </div>

          <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--text-3)' }}>
            ONE TAP. LET’S GO →
          </p>
        </Card>

        {/* Subtle "skip" — only for parents or testers. The kid is never told
            this is here. Renders as a tiny non-button to avoid kid taps. */}
        {pickedId === null && (
          <div style={{ textAlign: 'center', marginTop: 10 }}>
            <button
              type="button"
              onClick={onDone}
              style={{
                background: 'transparent', border: 'none', color: 'var(--text-3)',
                fontSize: 11, fontFamily: 'inherit', cursor: 'pointer', opacity: 0.6,
              }}
            >
              skip
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
